'use client';
import React, { useState } from 'react';
import { Search, ChevronDown, CheckCircle, XCircle, AlertCircle, TrendingUp, Award, GitBranch, DollarSign, Calendar } from 'lucide-react';
import { DISTRIBUTORS, COMMISSION_HISTORY, getEBRate, getEBTierLabel, getRankDotColor, Distributor, Rank } from '@/data/mockData';
import RankBadge from '@/components/ui/RankBadge';
import CommissionHistoryChart from './CommissionHistoryChart';
import BonusBreakdownPanel from './BonusBreakdownPanel';

export default function DistributorProfileClient() {
  const [selectedId, setSelectedId] = useState('dist-001');
  const [searchOpen, setSearchOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [activeTab, setActiveTab] = useState<'overview' | 'downline' | 'commissions' | 'history'>('overview');

  const allDists = Object.values(DISTRIBUTORS);
  const filtered = query ? allDists.filter(d => d.name.toLowerCase().includes(query.toLowerCase()) || d.id.includes(query)) : allDists;
  const dist = DISTRIBUTORS[selectedId];

  if (!dist) return null;

  const formatNum = (n: number) => String(Math.round(n)).replace(/\B(?=(\d{3})+(?!\d))/g, ',');

  const sponsor = dist.sponsorId ? DISTRIBUTORS[dist.sponsorId] : null;
  const directDownline = dist.children.map(id => DISTRIBUTORS[id]).filter(Boolean);
  const ebRate = getEBRate(dist.gsv);
  const isExec = ['Executive', 'Provisional Executive', 'Emerald', 'Diamond', 'Blue Diamond', 'Presidential Director'].includes(dist.rank);

  const qualStages = [
    { key: 'LOI', label: 'LOI', sub: '1,000 GSV', req: 1000 },
    { key: 'Q1', label: 'Q1', sub: '1,500 GSV', req: 1500 },
    { key: 'Q2', label: 'Q2', sub: '2,000 GSV', req: 2000 },
    { key: 'Qualified', label: 'Exec', sub: 'Qualified', req: 2000 },
  ];
  const qualIdx = { None: -1, LOI: 0, Q1: 1, Q2: 2, Qualified: 3 }[dist.qualStage] ?? -1;

  const tabs = [
    { key: 'overview', label: 'Overview', icon: TrendingUp },
    { key: 'downline', label: 'Downline', icon: GitBranch },
    { key: 'commissions', label: 'Commissions', icon: DollarSign },
    { key: 'history', label: 'History', icon: Calendar },
  ] as const;

  return (
    <div className="flex flex-col h-full bg-zinc-950">
      {/* Header */}
      <div className="flex-shrink-0 px-6 py-4 border-b border-zinc-800 bg-zinc-900/40">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-lg font-bold text-zinc-100">Distributor Profile</h1>
            <p className="text-xs text-zinc-500 mt-0.5">Detailed view of individual distributor performance and compensation</p>
          </div>
          {/* Distributor Selector */}
          <div className="relative">
            <button
              onClick={() => setSearchOpen(!searchOpen)}
              className="flex items-center gap-2 bg-zinc-800 border border-zinc-700 rounded-xl px-4 py-2 text-sm text-zinc-200 hover:border-zinc-600 transition-colors"
            >
              <div className={`w-2 h-2 rounded-full ${getRankDotColor(dist.rank)}`} />
              <span className="font-medium">{dist.name}</span>
              <span className="text-zinc-600 font-mono text-xs">{dist.id}</span>
              <ChevronDown size={14} className="text-zinc-500" />
            </button>
            {searchOpen && (
              <div className="absolute right-0 top-full mt-1 z-50 w-72 bg-zinc-800 border border-zinc-700 rounded-xl shadow-2xl animate-fade-in">
                <div className="p-2 border-b border-zinc-700">
                  <div className="flex items-center gap-2 bg-zinc-700/50 rounded-lg px-3 py-1.5">
                    <Search size={13} className="text-zinc-500" />
                    <input
                      autoFocus
                      type="text"
                      placeholder="Search by name or ID..."
                      value={query}
                      onChange={e => setQuery(e.target.value)}
                      className="bg-transparent text-sm text-zinc-200 placeholder-zinc-600 outline-none w-full"
                    />
                  </div>
                </div>
                <div className="max-h-60 overflow-y-auto scrollbar-thin py-1">
                  {filtered.map(d => (
                    <button
                      key={`sel-${d.id}`}
                      onClick={() => { setSelectedId(d.id); setSearchOpen(false); setQuery(''); }}
                      className={`w-full flex items-center gap-3 px-3 py-2 text-left hover:bg-zinc-700/50 transition-colors ${selectedId === d.id ? 'bg-violet-600/10' : ''}`}
                    >
                      <div className={`w-2 h-2 rounded-full flex-shrink-0 ${getRankDotColor(d.rank)}`} />
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-semibold text-zinc-200 truncate">{d.name}</p>
                        <p className="text-[10px] text-zinc-600">{d.rank}</p>
                      </div>
                      <span className="text-[10px] font-mono text-zinc-600">{d.id}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto scrollbar-thin">
        <div className="max-w-screen-2xl mx-auto px-6 py-5 space-y-5">
          {/* Profile Header Card */}
          <div className="bg-zinc-900 border border-zinc-800 rounded-2xl p-5">
            <div className="flex items-start gap-5">
              <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-violet-600/30 to-violet-800/20 border border-violet-500/20 flex items-center justify-center text-2xl font-bold text-violet-300 flex-shrink-0">
                {dist.name.split(' ').map(n => n[0]).join('')}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-3 flex-wrap">
                  <h2 className="text-xl font-bold text-zinc-100">{dist.name}</h2>
                  <RankBadge rank={dist.rank} size="md" />
                  <span className={`flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full border ${dist.active ? 'text-emerald-400 bg-emerald-500/10 border-emerald-500/20' : 'text-red-400 bg-red-500/10 border-red-500/20'}`}>
                    {dist.active ? <CheckCircle size={11} /> : <XCircle size={11} />}
                    {dist.active ? 'Active' : 'Inactive'}
                  </span>
                  {dist.pexecMonthsRemaining !== null && (
                    <span className="flex items-center gap-1 text-xs font-semibold text-orange-400 bg-orange-500/10 border border-orange-500/20 px-2 py-0.5 rounded-full">
                      <AlertCircle size={11} />
                      PEXEC — {dist.pexecMonthsRemaining}mo remaining
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-4 mt-2 flex-wrap">
                  <span className="text-xs text-zinc-500 font-mono">{dist.id}</span>
                  <span className="text-xs text-zinc-500">{dist.email}</span>
                  <span className="text-xs text-zinc-500">Joined {dist.joinDate}</span>
                  <span className="text-xs text-zinc-500">Country: {dist.country}</span>
                  {sponsor && (
                    <span className="text-xs text-zinc-500">Sponsor: <span className="text-violet-400">{sponsor.name}</span></span>
                  )}
                </div>
              </div>
              <div className="flex flex-col items-end gap-1 flex-shrink-0">
                <span className="text-[10px] text-zinc-600 uppercase tracking-wider">EB Tier</span>
                <span className="text-2xl font-bold text-amber-400 font-mono">{getEBTierLabel(dist.gsv)}</span>
                <span className="text-[10px] text-zinc-600">on {(dist.gsv/1000).toFixed(1)}K GSV</span>
              </div>
            </div>

            {/* 8 Key Stats */}
            <div className="grid grid-cols-8 gap-2 mt-4">
              {[
                { label: 'PSV', value: dist.psv.toString(), ok: dist.psv >= 100, mono: true },
                { label: 'GSV', value: `${(dist.gsv/1000).toFixed(1)}K`, ok: true, mono: true },
                { label: 'G1 Execs', value: dist.g1BreakawayCount.toString(), ok: true, mono: true },
                { label: 'Direct Team', value: dist.children.length.toString(), ok: true, mono: true },
                { label: 'Commission', value: `$${formatNum(dist.commissionThisMonth)}`, ok: true, mono: true },
                { label: 'ADR', value: dist.adrActive ? 'Active' : 'Off', ok: dist.adrActive, mono: false },
                { label: 'Grace Month', value: dist.graceMonthUsed ? 'Used' : 'Available', ok: !dist.graceMonthUsed, mono: false },
                { label: 'Qual Stage', value: dist.qualStage === 'Qualified' ? 'Exec' : dist.qualStage === 'None' ? 'N/A' : dist.qualStage, ok: dist.qualStage === 'Qualified', mono: false },
              ].map(s => (
                <div key={`kstat-${s.label}`} className="bg-zinc-800/50 rounded-xl p-2.5 text-center">
                  <p className="text-[9px] uppercase tracking-wider text-zinc-600 font-semibold">{s.label}</p>
                  <p className={`text-sm font-bold mt-1 ${s.mono ? 'font-mono' : ''} ${s.ok ? 'text-zinc-100' : 'text-red-400'}`}>{s.value}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Qualification Track */}
          {dist.qualStage !== 'None' && (
            <div className="bg-zinc-900 border border-zinc-800 rounded-2xl p-5">
              <div className="flex items-center gap-2 mb-4">
                <Award size={15} className="text-amber-400" />
                <h3 className="text-sm font-semibold text-zinc-300">Executive Qualification Track</h3>
              </div>
              <div className="flex items-center gap-0">
                {qualStages.map((stage, i) => {
                  const done = i <= qualIdx;
                  const current = i === qualIdx;
                  return (
                    <React.Fragment key={`qs-${stage.key}`}>
                      <div className="flex flex-col items-center">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center border-2 font-bold text-xs transition-all ${
                          done ? 'bg-violet-600 border-violet-500 text-white' : 'bg-zinc-800 border-zinc-700 text-zinc-600'
                        } ${current ? 'ring-2 ring-violet-400 ring-offset-2 ring-offset-zinc-900' : ''}`}>
                          {done ? '✓' : i + 1}
                        </div>
                        <p className={`text-[10px] font-semibold mt-1.5 ${done ? 'text-violet-300' : 'text-zinc-600'}`}>{stage.label}</p>
                        <p className="text-[9px] text-zinc-700">{stage.sub}</p>
                      </div>
                      {i < qualStages.length - 1 && (
                        <div className={`flex-1 h-0.5 mx-1 mt-[-18px] ${i < qualIdx ? 'bg-violet-600' : 'bg-zinc-800'}`} />
                      )}
                    </React.Fragment>
                  );
                })}
              </div>
              <div className="mt-4 bg-zinc-800/40 rounded-xl p-3 grid grid-cols-3 gap-3">
                {[
                  { label: 'Month 1 (LOI)', req: '100 PSV + ADR + 1,000 GSV', done: qualIdx >= 0 },
                  { label: 'Month 2 (Q1)', req: '100 PSV + ADR + 1,500 GSV', done: qualIdx >= 1 },
                  { label: 'Month 3 (Q2)', req: '100 PSV + ADR + 2,000 GSV', done: qualIdx >= 2 },
                ].map(m => (
                  <div key={`qm-${m.label}`} className={`rounded-lg p-2 border ${m.done ? 'bg-violet-600/10 border-violet-500/20' : 'bg-zinc-800/30 border-zinc-700'}`}>
                    <p className={`text-[10px] font-semibold ${m.done ? 'text-violet-300' : 'text-zinc-500'}`}>{m.label}</p>
                    <p className="text-[9px] text-zinc-600 mt-0.5">{m.req}</p>
                    <p className={`text-[10px] font-bold mt-1 ${m.done ? 'text-emerald-400' : 'text-zinc-600'}`}>{m.done ? '✓ Met' : 'Pending'}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Tabs */}
          <div className="border-b border-zinc-800">
            <div className="flex items-center gap-1">
              {tabs.map(tab => (
                <button
                  key={`tab-${tab.key}`}
                  onClick={() => setActiveTab(tab.key)}
                  className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-all duration-150 ${
                    activeTab === tab.key
                      ? 'border-violet-500 text-violet-300'
                      : 'border-transparent text-zinc-500 hover:text-zinc-300'
                  }`}
                >
                  <tab.icon size={14} />
                  {tab.label}
                </button>
              ))}
            </div>
          </div>

          {/* Tab Content */}
          {activeTab === 'overview' && (
            <div className="grid grid-cols-2 gap-5">
              <BonusBreakdownPanel distributor={dist} />
              <CommissionHistoryChart data={COMMISSION_HISTORY} />
            </div>
          )}

          {activeTab === 'downline' && (
            <div className="bg-zinc-900 border border-zinc-800 rounded-2xl overflow-hidden">
              <div className="px-4 py-3 border-b border-zinc-800 flex items-center justify-between">
                <h3 className="text-sm font-semibold text-zinc-300">Direct Downline ({directDownline.length})</h3>
              </div>
              <table className="w-full">
                <thead>
                  <tr className="border-b border-zinc-800">
                    {['Name', 'ID', 'Rank', 'PSV', 'GSV', 'Qual Stage', 'Status', 'Commission'].map(h => (
                      <th key={`ddl-th-${h}`} className="text-left text-[9px] font-semibold uppercase tracking-wider text-zinc-600 px-4 py-2.5">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {directDownline.map(child => (
                    <tr key={`ddl-${child.id}`} className="border-b border-zinc-800/40 hover:bg-zinc-800/30 transition-colors">
                      <td className="px-4 py-2.5">
                        <div className="flex items-center gap-2">
                          <div className={`w-2 h-2 rounded-full ${getRankDotColor(child.rank)}`} />
                          <span className="text-sm font-medium text-zinc-200">{child.name}</span>
                        </div>
                      </td>
                      <td className="px-4 py-2.5"><span className="text-xs font-mono text-zinc-500">{child.id}</span></td>
                      <td className="px-4 py-2.5"><RankBadge rank={child.rank} size="xs" /></td>
                      <td className="px-4 py-2.5"><span className={`text-sm font-mono font-semibold ${child.psv >= 100 ? 'text-zinc-300' : 'text-red-400'}`}>{child.psv}</span></td>
                      <td className="px-4 py-2.5"><span className="text-sm font-mono text-zinc-300">{(child.gsv/1000).toFixed(1)}K</span></td>
                      <td className="px-4 py-2.5">
                        <span className={`text-xs font-semibold px-2 py-0.5 rounded ${
                          child.qualStage === 'Qualified' ? 'bg-purple-500/15 text-purple-400' :
                          child.qualStage === 'None'? 'bg-zinc-700/40 text-zinc-500' : 'bg-amber-500/15 text-amber-400'
                        }`}>{child.qualStage}</span>
                      </td>
                      <td className="px-4 py-2.5">
                        <span className={`text-xs font-semibold ${child.active ? 'text-emerald-400' : 'text-red-400'}`}>
                          {child.active ? '● Active' : '○ Inactive'}
                        </span>
                      </td>
                      <td className="px-4 py-2.5"><span className="text-sm font-mono font-semibold text-emerald-400">${formatNum(child.commissionThisMonth)}</span></td>
                    </tr>
                  ))}
                  {directDownline.length === 0 && (
                    <tr><td colSpan={8} className="px-4 py-8 text-center text-sm text-zinc-600">No direct downline members for this distributor</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          )}

          {activeTab === 'commissions' && (
            <div className="grid grid-cols-2 gap-5">
              <BonusBreakdownPanel distributor={dist} detailed />
              <CommissionHistoryChart data={COMMISSION_HISTORY} />
            </div>
          )}

          {activeTab === 'history' && (
            <CommissionHistoryChart data={COMMISSION_HISTORY} fullWidth />
          )}
        </div>
      </div>
    </div>
  );
}